/**
 * Admin user-management queries — user detail drawer, admin toggle,
 * personalization/onboarding resets, and the chat admin testing panel
 * profile read/write helpers.
 */
import { supabase } from '@/lib/supabase'

/** Fetch detailed user info via admin RPC. */
export async function fetchUserDetail(userId) {
  const { data, error } = await supabase.rpc('admin_get_user_detail', { p_user_id: userId })
  if (error) throw error
  return data
}

/** Toggle a user's admin status via the super-admin-only RPC.
 *  Server enforces that the caller is the super-admin (see migration
 *  20260421184600_admin_set_is_admin_rpc). The client's isSuperAdmin check
 *  + confirm dialog remain as defense-in-depth. */
export async function toggleAdminStatus(userId, isAdmin) {
  const { error } = await supabase.rpc('admin_set_is_admin', {
    target_user_id: userId,
    new_is_admin: isAdmin,
  })
  if (error) throw error
}

/** Reset a target user's personalization state (lighter action). Nulls out
 *  first-visit flags + last-use tabs/books so personalization recomputes on
 *  their next login, but keeps onboarding answers intact. Server-side admin
 *  check via SECURITY DEFINER RPC — see migration
 *  20260421195000_admin_reset_onboarding_rpcs. */
export async function resetUserPersonalization(userId) {
  const { error } = await supabase.rpc('admin_reset_user_personalization', {
    target_user_id: userId,
  })
  if (error) throw error
}

/** Reset a target user's entire onboarding (nuclear action). Nulls out all
 *  personalization state PLUS every profile answer collected during the
 *  wizard + onboarding_completed = false, forcing the full onboarding flow
 *  on next login. Server-side admin check via SECURITY DEFINER RPC. */
export async function resetUserOnboarding(userId) {
  const { error } = await supabase.rpc('admin_reset_user_onboarding', {
    target_user_id: userId,
  })
  if (error) throw error
}

/** Fetch admin testing profile data. */
export async function fetchTestingProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('is_premium, monthly_messages_used')
    .eq('id', userId)
    .single()
  if (error) throw error
  return data
}

/** Update profile flags for admin testing. */
export async function updateTestingProfile(userId, updates) {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select('is_premium, monthly_messages_used')
  if (error) throw error
  return data?.[0]
}
